/**
* @ngdoc service
* @name  sharebase.sbDocumentDetailService
* 
* @description 
* A service used to retrieve and interact with ShareBase
* document details
**/
export function DocumentDetailServiceFactory(
	$http:ng.IHttpService, 
	settingsService, 
	sbDocumentService, 
	gettextCatalog:ng.gettext.gettextCatalog
) {
	// mappings of API friendly file names to translated ones;
	// doesn't include proper noun names e.g. 'Acrobat PDF'
	const fileFormatTranslations = {
		'Image': gettextCatalog.getString('Image'),
		'Text Document': gettextCatalog.getString('Text Document'),
		'Rich Text Document': gettextCatalog.getString('Rich Text Document'),
		'Comma Separated Values' : gettextCatalog.getString('Comma Separated Values'),
		'Video' : gettextCatalog.getString('Video'),
		'Audio File' : gettextCatalog.getString('Audio File'),
	};

	return {
		/**
		* @ngdoc method
		* @name sbDocumentDetailService#getDocumentDetails
		* @methodOf sharebase.sbDocumentDetailService
		* @kind function
		*
		* @description
		* Gets a specific document's details to the user
		* 
		* @param {object} documentId A Document ID
		*
		* @returns {Deferred} A promise that will resolve with a document's details
		* 
		*/
		getDocumentDetails,

		/**
		* @ngdoc method
		* @name sbDocumentDetailService#getActivities
		* @methodOf sharebase.sbDocumentDetailService
		* @kind function
		*
		* @description
		* Gets a specific document's activities to the user
		* 
		* @param {object} documentId A Document ID
		*
		* @returns {Deferred} A promise that will resolve with a document's activities
		* 
		*/
		getActivities,

		/**
		* @ngdoc method
		* @name sbDocumentDetailService#getComments
		* @methodOf sharebase.sbDocumentDetailService
		* @kind function
		*
		* @description
		* Gets a specific document's comments to the user
		* 
		* @param {object} documentId A Document ID
		*
		* @returns {Deferred} A promise that will resolve with a document's comments
		* 
		*/
		getComments,
		
		/**
		* @ngdoc method
		* @name sbDocumentDetailService#getVersions
		* @methodOf sharebase.sbDocumentDetailService
		* @kind function
		*
		* @description
		* Gets a specific document's versions to the user
		* 
		* @param {object} documentId A Document ID
		*
		* @returns {Deferred} A promise that will resolve with a document's versions
		* 
		*/
		getVersions,

		/**
		* @ngdoc method
		* @name sbDocumentDetailService#getSharedLinks
		* @methodOf sharebase.sbDocumentDetailService
		* @kind function
		*
		* @description
		* Gets a specific document's shared links to the user
		* 
		* @param {object} documentId A Document ID
		*
		* @returns {Deferred} A promise that will resolve with a document's shared links
		* 
		*/
		getSharedLinks,

		/**
		* @ngdoc method
		* @name sbDocumentDetailService#getSharedUsers
		* @methodOf sharebase.sbDocumentDetailService
		* @kind function
		*
		* @description
		* Gets a specific document's shared users to the user
		* 
		* @param {object} documentId A Document ID
		*
		* @returns {Deferred} A promise that will resolve with a document's shared users
		* 
		*/
		getSharedUsers
	};

	function getDocumentDetails(documentId) {
		return sbDocumentService.getDocument(documentId)
			.then(function (response) {
				var doc = response.Embedded.Document;
				var results = {
					dateCreated: doc.DateCreated,
					dateModified: doc.DateModified,
					fileFormat: fileFormatTranslations[doc.FileFormat] || doc.FileFormat,
					fileSize: doc.FileSize,
					owner: doc.Owner
				};

				return results;
			});
	}

	function getActivities(documentId) {
		return $http.get(settingsService.serverAPIPath + '/documents/' + documentId + '/activities?top=1000')
			.then(function (response) {
				return response.data;
			});
	}

	function getComments(documentId) {
		return $http.get(settingsService.serverAPIPath + '/documents/' + documentId + '/comments')
			.then(function (response) {
				return response.data;
			});
	}

	function getVersions(documentId) {
		return $http.get(settingsService.serverAPIPath + '/documents/' + documentId + '/revisions')
			.then(function (results) {
				return results.data;
			});
	}
	
	function getSharedLinks(documentId) {
		return $http.get(settingsService.serverAPIPath + '/documents/' + documentId + '/shares/all')
			.then(function (response) {
				return response.data;
			});
	}

	function getSharedUsers(documentId) {
		return $http.get(settingsService.serverAPIPath + '/documents/' + documentId + '/permissions')
			.then(function (response) {
				return response.data;
			});
	}
}

DocumentDetailServiceFactory.$inject = [
	'$http',
	'settingsService',
	'sbDocumentService',
	'gettextCatalog'
];


// WEBPACK FOOTER //
// ./src/sharebase/services/documentDetailService.ts